import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { BadgeCheck, Radio, ExternalLink, CalendarClock, Filter, Zap } from "lucide-react";

const TICKER_SPEED_PX_S = 55;

function fmt(dt) {
  const d = new Date(dt);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function relativeIn(dt) {
  const ms = new Date(dt).getTime() - Date.now();
  if (ms <= 0) return "LIVE";
  const m = Math.round(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `in ${h}h`;
  return `in ${Math.round(h / 24)}d`;
}

export default function Streams() {
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState([]);
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  const [tickerWidth, setTickerWidth] = useState(0);
  const tickerRef = useRef(null);

  const load = async () => {
    const [{ data }, { data: liveRows }] = await Promise.all([
      api.get(`/streams/upcoming${verifiedOnly ? "?verified_only=true" : ""}`),
      api.get("/streams/live"),
    ]);
    setEvents(data);
    setLive(liveRows);
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [verifiedOnly]);

  useEffect(() => {
    if (tickerRef.current) setTickerWidth(tickerRef.current.scrollWidth);
  }, [events]);

  const tickerDuration = Math.max(20, Math.round((tickerWidth || 1) / TICKER_SPEED_PX_S));

  const verifiedCount = useMemo(() => events.filter((e) => e.streamer_verified).length, [events]);

  const featured = events[0];

  return (
    <div className="space-y-10" data-testid="streams-root">
      <style>{`
        @keyframes legacy-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .legacy-ticker-track {
          animation: legacy-ticker linear infinite;
        }
        @keyframes legacy-glow {
          0%,100% { box-shadow: 0 0 0 0 rgba(0,240,255,0); }
          50%     { box-shadow: 0 0 24px 4px rgba(176,132,255,0.35); }
        }
      `}</style>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[#B084FF] font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5" /> / STREAM WALL
          </div>
          <h1 className="font-heading text-4xl lg:text-6xl font-black tracking-tighter uppercase leading-none">
            Every broadcast.<br />
            <span className="text-cyan-400">One neon strip.</span>
          </h1>
          <p className="text-gray-400 mt-3 max-w-xl text-sm">
            Every scheduled broadcast{verifiedOnly ? " from verified streamers" : ""} — kickoff times, games, and jump-in links.
          </p>
        </div>
        <button
          onClick={() => setVerifiedOnly((v) => !v)}
          data-testid="streams-verified-toggle"
          className={`flex items-center gap-2 px-4 py-2 border rounded-none text-xs font-bold uppercase tracking-widest font-mono transition-colors ${
            verifiedOnly
              ? "border-cyan-400 text-cyan-400"
              : "border-[#232330] text-gray-500 hover:text-white"
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          {verifiedOnly ? "Verified only" : "All streamers"}
        </button>
      </div>

      {/* LIVE NOW */}
      {live.length > 0 && (
        <section data-testid="streams-live-now" className="border border-red-500/60 bg-red-500/10 p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-mono font-bold tracking-widest px-2 py-1">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
            </span>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-red-300">
              {live.length} streamer{live.length === 1 ? "" : "s"} online right now
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {live.map((s) => (
              <a key={s.id} href={s.channel_url} target="_blank" rel="noreferrer"
                 data-testid={`live-card-${s.handle}`}
                 className="flex items-center gap-3 border border-red-500/40 bg-[#101015] hover:border-red-500 p-3 transition-colors">
                {s.avatar_url ? (
                  <img src={s.avatar_url} alt={s.handle} className="w-12 h-12 object-cover border border-red-500/40" />
                ) : (
                  <div className="w-12 h-12 border border-red-500/40 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-red-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-black uppercase tracking-tight truncate flex items-center gap-1.5">
                    {s.handle}
                    {s.verified && <BadgeCheck className="w-3.5 h-3.5 text-cyan-400 shrink-0" />}
                  </div>
                  {s.live_title && <div className="text-xs text-gray-400 truncate">{s.live_title}</div>}
                  <div className="text-[10px] font-mono uppercase tracking-widest text-red-300 mt-0.5">
                    {s.platform} · {s.game}
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-red-400 shrink-0" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Neon ticker */}
      <div
        className="relative overflow-hidden border-y border-[#B084FF]/40 bg-gradient-to-r from-[#101015] via-[#0f0a1a] to-[#101015] py-4"
        data-testid="streams-ticker"
        style={{ animation: "legacy-glow 3s ease-in-out infinite" }}
      >
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#05050A] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#05050A] to-transparent z-10" />
        {events.length === 0 ? (
          <div className="text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            No streams on the wall yet. Verified streamers can schedule from their dossier.
          </div>
        ) : (
          <div
            ref={tickerRef}
            className="legacy-ticker-track flex gap-4 whitespace-nowrap"
            style={{ animationDuration: `${tickerDuration}s` }}
          >
            {[...events, ...events].map((ev, i) => (
              <a
                key={`${ev.id}-${i}`}
                href={ev.stream_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`ticker-ev-${ev.id}`}
                className="group inline-flex items-center gap-3 border border-[#232330] hover:border-[#B084FF] bg-[#101015] px-4 py-2 min-w-fit transition-colors"
              >
                <div className="w-2 h-2 rounded-full bg-[#B084FF] group-hover:animate-pulse" />
                <span className="font-heading font-black uppercase tracking-tight text-sm">
                  {ev.streamer_handle}
                </span>
                {ev.streamer_verified && <BadgeCheck className="w-3.5 h-3.5 text-cyan-400" />}
                <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                  {ev.game}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">
                  {relativeIn(ev.scheduled_at)}
                </span>
                {ev.title && (
                  <span className="text-[10px] font-mono text-cyan-400 max-w-[200px] truncate">
                    {ev.title}
                  </span>
                )}
                <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-[#B084FF]" />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Featured broadcast */}
      {featured && (
        <section data-testid="streams-featured" className="grid lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-5 border border-[#B084FF]/40 bg-[#B084FF]/5 p-6 flex flex-col justify-between">
            <div>
              <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#B084FF] mb-2">NEXT ON THE WALL</div>
              <div className="font-heading font-black text-3xl uppercase tracking-tighter leading-none">
                {featured.streamer_handle}
                {featured.streamer_verified && (
                  <BadgeCheck className="inline-block w-6 h-6 text-cyan-400 ml-2 -mt-1" />
                )}
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-gray-400 mt-3">
                {featured.game} · {featured.streamer_platform || "Live"} · {featured.streamer_country || "—"}
              </div>
              {featured.title && (
                <div className="mt-4 text-gray-200 leading-relaxed">{featured.title}</div>
              )}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Kickoff</div>
                <div className="font-heading font-black text-xl text-[#F4D03F] mt-1 flex items-center gap-2">
                  <CalendarClock className="w-4 h-4" /> {fmt(featured.scheduled_at)}
                </div>
              </div>
              <a href={featured.stream_url} target="_blank" rel="noreferrer"
                 className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black px-5 py-2.5 uppercase tracking-widest text-xs font-bold flex items-center gap-2">
                <ExternalLink className="w-3.5 h-3.5" /> Jump in
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {events.slice(1, 5).map((ev) => (
              <a
                key={ev.id}
                href={ev.stream_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`stream-card-${ev.id}`}
                className="border border-[#232330] bg-[#101015] hover:border-[#B084FF] p-4 transition-colors flex flex-col justify-between"
              >
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">
                    {fmt(ev.scheduled_at)}
                  </div>
                  <div className="font-heading font-black text-lg uppercase tracking-tight mt-2 flex items-center gap-2">
                    {ev.streamer_handle}
                    {ev.streamer_verified && <BadgeCheck className="w-4 h-4 text-cyan-400" />}
                  </div>
                  {ev.title && <div className="text-xs text-gray-400 mt-1 truncate">{ev.title}</div>}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                    {ev.game}
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">
                    {relativeIn(ev.scheduled_at)}
                  </span>
                </div>
              </a>
            ))}
            {events.length < 2 && (
              <div className="sm:col-span-2 border border-dashed border-[#232330] p-8 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
                One in the tank. More broadcasts drop as streamers schedule.
              </div>
            )}
          </div>
        </section>
      )}

      {/* Full schedule */}
      <section className="space-y-2" data-testid="streams-schedule">
        <div className="flex items-center justify-between px-1">
          <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase">
            Full schedule · {events.length} broadcasts · {verifiedCount} verified
          </div>
        </div>
        {events.length === 0 && (
          <div className="border border-[#232330] p-10 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            No broadcasts scheduled yet.
          </div>
        )}
        {events.map((ev) => (
          <div key={ev.id} data-testid={`stream-row-${ev.id}`}
               className="border border-[#232330] bg-[#101015] p-4 flex flex-wrap items-center justify-between gap-3 hover:border-[#B084FF] transition-colors">
            <div className="flex items-center gap-3">
              {ev.streamer_avatar_url ? (
                <img src={ev.streamer_avatar_url} alt={ev.streamer_handle}
                     className="w-10 h-10 object-cover border border-[#232330]" />
              ) : (
                <div className="w-10 h-10 border border-[#232330] bg-[#05050A] flex items-center justify-center">
                  <Radio className="w-4 h-4 text-gray-600" />
                </div>
              )}
              <div>
                <div className="font-heading font-black text-lg uppercase tracking-tight flex items-center gap-2">
                  {ev.streamer_handle}
                  {ev.streamer_verified && <BadgeCheck className="w-4 h-4 text-cyan-400" />}
                </div>
                <div className="text-xs text-gray-500 font-mono">
                  {ev.game} · {ev.streamer_platform || "—"} · {fmt(ev.scheduled_at)}
                </div>
                {ev.title && <div className="text-xs text-gray-400 mt-1">{ev.title}</div>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">
                {relativeIn(ev.scheduled_at)}
              </span>
              <a href={ev.stream_url} target="_blank" rel="noreferrer"
                 className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 border border-cyan-400/40 hover:border-cyan-300 px-3 py-1.5 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Watch
              </a>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
